import type { ITicketListItem, TicketStatus } from '@/shared/api/types/ticket-types'
import type { TicketType } from '@/shared/api/types/ticket-config-types'
import { useNavigate } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useTenantsQuery } from '@/entities/tenant'
import { useTicketsQuery } from '@/entities/ticket'
import { DEFAULT_PAGE_SIZE } from '@/shared/constants/pagination'
import { useScopedAdminPath } from '@/shared/hooks/use-scoped-admin-path'
import { generatePath } from '@/shared/libs/generate-path'
import { Input } from '@/shared/ui/input'
import { DataTableFilterField, DataTableToolbar } from '@/shared/ui/data-table'
import { PageContainer } from '@/shared/ui/page-container'
import { PageHeader } from '@/shared/ui/page-header'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select'
import { TicketListTable } from '@/features/ticket/ticket-list-table'

const ALL = 'all'
const TICKET_TYPES: TicketType[] = ['paid', 'free']
const TICKET_STATUSES: TicketStatus[] = ['inactive', 'active', 'used', 'expired', 'cancelled']

export function TicketListPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const scopedAdminPath = useScopedAdminPath()

  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [tenantId, setTenantId] = useState(ALL)
  const [ticketType, setTicketType] = useState(ALL)
  const [status, setStatus] = useState(ALL)
  const [keyword, setKeyword] = useState('')

  const { data: tenantsResponse } = useTenantsQuery({
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    isActive: true,
  })
  const tenants = useMemo(
    () => (tenantsResponse?.data ?? []).filter((tenant) => tenant.name !== 'Default'),
    [tenantsResponse?.data],
  )
  const tenantNames = useMemo(
    () => Object.fromEntries(tenants.map((tenant) => [tenant.id, tenant.name])),
    [tenants],
  )

  const { data, isLoading } = useTicketsQuery({
    current: page,
    pageSize,
    ...(tenantId !== ALL ? { tenantId } : {}),
    ...(ticketType !== ALL ? { ticketType: ticketType as TicketType } : {}),
    ...(status !== ALL ? { status: status as TicketStatus } : {}),
    ...(keyword.trim() ? { keyword: keyword.trim() } : {}),
  })

  function resetPageThen<T>(setter: (value: T) => void) {
    return (value: T) => {
      setPage(1)
      setter(value)
    }
  }

  function handleOpen(ticket: ITicketListItem) {
    void navigate({ href: scopedAdminPath(generatePath('/tickets/:id', { id: ticket.id })) })
  }

  return (
    <PageContainer>
      <PageHeader title={t('ticket.title', { defaultValue: 'Tickets' })} />

      <DataTableToolbar>
        <DataTableFilterField label={t('sidebar.tenants')}>
          <Select value={tenantId} onValueChange={resetPageThen(setTenantId)}>
            <SelectTrigger className="w-60">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t('common.all')}</SelectItem>
              {tenants.map((tenant) => (
                <SelectItem key={tenant.id} value={tenant.id}>
                  {tenant.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </DataTableFilterField>
        <DataTableFilterField label={t('ticketConfig.ticketType', { defaultValue: 'Type' })}>
          <Select value={ticketType} onValueChange={resetPageThen(setTicketType)}>
            <SelectTrigger className="w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t('common.all')}</SelectItem>
              {TICKET_TYPES.map((type) => (
                <SelectItem key={type} value={type}>
                  {t(`ticketConfig.${type}`, { defaultValue: type })}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </DataTableFilterField>
        <DataTableFilterField label={t('common.status')}>
          <Select value={status} onValueChange={resetPageThen(setStatus)}>
            <SelectTrigger className="w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t('common.all')}</SelectItem>
              {TICKET_STATUSES.map((value) => (
                <SelectItem key={value} value={value}>
                  {t(`ticket.status.${value}`, { defaultValue: value })}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </DataTableFilterField>
        <DataTableFilterField label={t('common.search')}>
          <Input
            value={keyword}
            placeholder={t('ticket.searchPlaceholder', { defaultValue: 'Order or kiosk ID' })}
            onChange={(event) => resetPageThen(setKeyword)(event.target.value)}
            className="w-56"
          />
        </DataTableFilterField>
      </DataTableToolbar>

      <TicketListTable
        data={data?.data.data ?? []}
        loading={isLoading}
        tenantNames={tenantNames}
        pagination={{
          current: page,
          pageSize,
          total: data?.data.total ?? 0,
          onChange: (nextPage, nextPageSize) => {
            setPage(nextPage)
            setPageSize(nextPageSize)
          },
        }}
        onOpen={handleOpen}
      />
    </PageContainer>
  )
}
